"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const uuid_1 = require("uuid");
const alerts_1 = require("../alerts");
const assets_1 = require("../assets");
const chat_1 = require("../chat");
const ChatManager_1 = __importDefault(require("../chat/ChatManager"));
const AutoAckMapper_1 = require("../mappers/AutoAckMapper");
const mappers_1 = require("../mappers");
const MessageUtil_1 = require("../message/MessageUtil");
const MapperUtils_1 = require("../utils/MapperUtils");
const models_1 = require("../models");
const NetworkManager_1 = require("../network/NetworkManager");
const NotificationManager_1 = require("../notification/NotificationManager");
const roleAccess_1 = require("../roleAccess/roleAccess");
const socket_1 = require("../socket");
const Observable_1 = require("../socket/Observable");
const SocketListener_1 = require("../socket/SocketListener");
const SocketNetworkListener_1 = require("../socket/SocketNetworkListener");
const starredMessages_1 = require("../starredMessages");
const store_1 = require("../store");
const user_1 = require("../user");
const utils_1 = require("../utils");
const DomainEvents_1 = require("../utils/DomainEvents");
const SocketManager_1 = require("../socket/SocketManager");
const LabsManager_1 = require("../chat/labs/LabsManager");
const EMRManager_1 = require("../chat/emr/EMRManager");
const AllLabRequisitionsManager_1 = require("../labs/AllLabRequisitionsManager");
const SyncQueueManager_1 = require("../syncQueue/SyncQueueManager");
class OrganizationUser {
    constructor(organization, config, adapterFactory, accessTokenProvider, internetStatusManager) {
        this.changeListeners = [];
        this.removeListeners = [];
        // Concurrent callers writing identical settings attach to the same in-flight PUT.
        this.settingsInFlight = new Map();
        // Concurrent callers fetching identical schedules attach to the same in-flight GET.
        this.scheduledVisitsInFlight = new Map();
        this.organization = organization;
        this.config = config;
        this.httpAdapter = adapterFactory.createOrgHttpAdapter(organization.organizationId);
        this.socketAdapter =
            adapterFactory.createSocketAdapter(accessTokenProvider);
        this.networkManager = new NetworkManager_1.NetworkManager(this.organization.organizationId, this.socketAdapter, internetStatusManager);
        this.storageAdapter = adapterFactory.createStorageAdapter(this.httpAdapter);
        this.assetManager = new assets_1.AssetManager(this.httpAdapter, adapterFactory, this.socketAdapter, accessTokenProvider, this.storageAdapter);
        this.exportChat = new chat_1.ExportChat(this.httpAdapter);
        this.allLabRequisitionsManager = new AllLabRequisitionsManager_1.AllLabRequisitionsManager(this.httpAdapter);
        this.syncQueueManager = new SyncQueueManager_1.SyncQueueManager(this.httpAdapter);
        this.hhnStorage = new store_1.HhnStorage(this.organization.organizationId, this.networkManager);
        this._recentContactsByBucket = this.createEmptyRecentContactsBuckets();
        this._organizationMediaData = {
            data: [],
            limit: 20,
            page: 0,
            total: 0,
            type: [],
        };
        this.notificationManager = new NotificationManager_1.NotificationManager(this);
        this.socketManager = new SocketManager_1.SocketManager(this.organization.organizationId, this, this.socketAdapter);
        this._quickReplies = organization.userProfile.settings.quickReplies || [];
    }
    registerChangeListener(cb) {
        const id = (0, uuid_1.v4)();
        this.changeListeners.push({ id, cb });
        return () => {
            this.changeListeners = this.changeListeners.filter(listner => listner.id !== id);
        };
    }
    getNotificationManager() {
        return this.notificationManager;
    }
    sameUser(userId) {
        return userId === this.getOrganization().userProfile.id;
    }
    groupNotificationSetting(groupType) {
        var _a, _b, _c;
        let groupSettings;
        switch (groupType) {
            case models_1.GROUP_TYPE.HIDDEN:
                return models_1.CHAT_NOTIFICATION_SETTING.NONE;
            case models_1.GROUP_TYPE.PATIENT:
                groupSettings =
                    (_a = this.organization.userProfile.settings) === null || _a === void 0 ? void 0 : _a.notification.patientGroup;
                break;
            case models_1.GROUP_TYPE.BROADCAST:
            case models_1.GROUP_TYPE.PRIVATE:
                groupSettings =
                    (_b = this.organization.userProfile.settings) === null || _b === void 0 ? void 0 : _b.notification.privateChat;
                break;
            case models_1.GROUP_TYPE.NON_PATIENT:
                groupSettings =
                    (_c = this.organization.userProfile.settings) === null || _c === void 0 ? void 0 : _c.notification.nonPatientGroup;
                break;
            default:
                throw new Error(`Not expected: no notification settings available for group type - ${groupType}`);
        }
        if (groupSettings === null || groupSettings === void 0 ? void 0 : groupSettings.all) {
            return models_1.CHAT_NOTIFICATION_SETTING.ALL;
        }
        if (groupSettings === null || groupSettings === void 0 ? void 0 : groupSettings.important) {
            return models_1.CHAT_NOTIFICATION_SETTING.IMPORTANT;
        }
        if (groupSettings === null || groupSettings === void 0 ? void 0 : groupSettings.none) {
            return models_1.CHAT_NOTIFICATION_SETTING.NONE;
        }
        return models_1.CHAT_NOTIFICATION_SETTING.ALL;
    }
    onDND() {
        return this.organization.userProfile.settings.doNotDisturb || false;
    }
    isExternalUser() {
        return [models_1.USER_TYPE.PATIENT, models_1.USER_TYPE.PHYSICIAN].includes(this.userDirectory.currentUserProfile.type);
    }
    init() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.isOrganizationActive()) {
                this.networkManager.init();
                // socket should be opened before the creation of domain objects that registers to sync callback (registerSocketAuthSuccessListener) - Alerts, ChatManager and UserDirectory
                yield this.open();
                this.userDirectory = new user_1.UserDirectory(this, this.httpAdapter);
                this.alert = new alerts_1.Alert(this, this.httpAdapter, this.userDirectory, this.hhnStorage, this.socketAdapter);
                this.starredMessage = new starredMessages_1.StarredMessage(this, this.httpAdapter, this.userDirectory, this.hhnStorage, this.socketAdapter);
                this.chatManager = new ChatManager_1.default(this.httpAdapter, this, this.socketAdapter, this.config, this.userDirectory, this.hhnStorage);
                this.labsManager = new LabsManager_1.LabsManager(this.httpAdapter);
                this.emrManager = new EMRManager_1.EMRManager(this.httpAdapter);
                this.userDirectory.init();
                yield this.userDirectory.fetchDirectory();
                this.alert.init();
                yield this.alert.syncAlerts();
            }
        });
    }
    get templates() {
        return this._templates;
    }
    open() {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.socketAdapter.open(this.organization, new SocketListener_1.SocketListener(this.userDirectory, this.organization), new SocketNetworkListener_1.SocketNetworkListener(this.organization.organizationId));
        });
    }
    getSocketManager() {
        return this.socketManager;
    }
    addSocketResponseErrorListener(cb) {
        return Observable_1.Observable.getInstance().subscribe((0, DomainEvents_1.getEventKey)(socket_1.DomainEvents.RESPONSE_ERROR, this.organization.organizationId), cb);
    }
    addSlowMessageSendListener(cb) {
        return Observable_1.Observable.getInstance().subscribe((0, DomainEvents_1.getEventKey)(socket_1.DomainEvents.SLOW_MESSAGE_SEND, this.organization.organizationId), cb);
    }
    getOrganization() {
        return this.organization;
    }
    isOrganizationActive() {
        return ((this.organization.organizationStatus === models_1.ORGANIZATION_STATUS.ACTIVE ||
            this.organization.organizationStatus === models_1.ORGANIZATION_STATUS.PENDING) &&
            this.organization.userProfile.status === models_1.USER_STATUS.ACTIVE);
    }
    getRecentContacts(bucket) {
        return this._recentContactsByBucket[bucket];
    }
    get organizationMedia() {
        return this._organizationMediaData.data;
    }
    getSignedUrl(url) {
        return __awaiter(this, void 0, void 0, function* () {
            const body = {
                url,
            };
            return yield this.httpAdapter.getCommonAdapter().getSignedUrl(body);
        });
    }
    fetchExportChatData(params, context) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.exportChat.fetchExportChatData(params, context);
        });
    }
    getExportChatData() {
        return this.exportChat.exportChatData;
    }
    fetchDepartments(organizationId) {
        return __awaiter(this, void 0, void 0, function* () {
            const storedDepartments = this.hhnStorage.commonStorage.getDepartments();
            if (storedDepartments && storedDepartments.length > 0) {
                return storedDepartments;
            }
            else {
                const departmentsResponse = yield this.httpAdapter
                    .getCommonAdapter()
                    .getDepartments({
                    limit: 100,
                    pageNo: 1,
                }, organizationId);
                const departmentData = departmentsResponse.map(departmentResponse => {
                    return (0, mappers_1.departmentMapper)(departmentResponse);
                });
                this.hhnStorage.commonStorage.setDepartments(departmentData);
                return departmentData;
            }
        });
    }
    getDepartments() {
        return this.hhnStorage.commonStorage.getDepartments();
    }
    countersListeners(cb) {
        console.log("Subscribing to counters for org:", this.getOrganization().organizationId, this.getOrganization().organizationName);
        return Observable_1.Observable.getInstance().subscribe((0, DomainEvents_1.getEventKey)(socket_1.DomainEvents.ORG_COUNTERS, this.getOrganization().organizationId), cb);
    }
    getCounters() {
        return this.hhnStorage.orgStorage.getCounters();
    }
    markMessageAsDeliveredListener(cb) {
        return Observable_1.Observable.getInstance().subscribe((0, DomainEvents_1.getEventKey)(socket_1.DomainEvents.MARK_AS_DELIVERED, this.organization.organizationId), cb);
    }
    getReferralSource(requestParam, context) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield this.httpAdapter
                .getCommonAdapter()
                .getReferralSource(requestParam, context);
        });
    }
    addReferralSource(name) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield this.httpAdapter.getCommonAdapter().addReferralSource(name);
        });
    }
    getCommunityLiaisoning(requestParam, context) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield this.httpAdapter
                .getCommonAdapter()
                .getCommunityLiaisoning(requestParam, context);
        });
    }
    addCommunityLiaisoning(name) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield this.httpAdapter
                .getCommonAdapter()
                .addCommunityLiaisoning(name);
        });
    }
    hasAccess(feature) {
        const role = this.organization.userProfile.type;
        const roleAccesses = roleAccess_1.roleAccess[role] || [];
        return roleAccesses.includes(feature);
    }
    getStorage() {
        return this.hhnStorage;
    }
    getNetworkManager() {
        return this.networkManager;
    }
    getOrgUniqueIdentifer() {
        return this.getOrganizationId();
    }
    getOrganizationId() {
        return this.getOrganization().organizationId;
    }
    isSameOrganization(orgData) {
        return this.getOrganizationId() === orgData.organizationId;
    }
    isOrganization(orgId) {
        return this.getOrganizationId() === orgId;
    }
    isActive() {
        return this.getOrganization().userProfile.status === models_1.USER_STATUS.ACTIVE;
    }
    updateDoNotDisturb(dnd) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.updateSettings({
                settings: {
                    doNotDisturb: dnd,
                },
            });
        });
    }
    updateNotificationSettings(notificationSettings) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.updateSettings({
                settings: {
                    notification: notificationSettings,
                },
            });
        });
    }
    updateSettings(settingsData) {
        return __awaiter(this, void 0, void 0, function* () {
            const key = JSON.stringify(settingsData);
            const existing = this.settingsInFlight.get(key);
            if (existing) {
                return existing;
            }
            const request = this.updateSettingsInternal(settingsData);
            this.settingsInFlight.set(key, request);
            try {
                return yield request;
            }
            finally {
                this.settingsInFlight.delete(key);
            }
        });
    }
    updateSettingsInternal(settingsData) {
        return __awaiter(this, void 0, void 0, function* () {
            const settings = yield this.httpAdapter
                .getOrganizationAdapter()
                .updateSettings(settingsData);
            this.organization = Object.assign(Object.assign({}, this.organization), { userProfile: Object.assign(Object.assign({}, this.organization.userProfile), { settings: Object.assign(Object.assign({}, this.organization.userProfile.settings), settings) }) });
            this.changeListeners.forEach(l => l.cb(this));
        });
    }
    fetchOrganizationMedia(params, context) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b;
            const searchTerm = (_a = params.search) !== null && _a !== void 0 ? _a : "";
            const searchType = params.type;
            const searchLimit = (_b = params.limit) !== null && _b !== void 0 ? _b : this._organizationMediaData.limit;
            const { page, total, data, searchTerm: currentSearchTerm, type: currentType, } = this._organizationMediaData;
            const normalizeTypes = (types) => [...(types !== null && types !== void 0 ? types : [])].sort();
            const currentTypes = normalizeTypes(currentType);
            const requestedTypes = normalizeTypes(searchType);
            const isNewSearch = currentSearchTerm !== searchTerm ||
                JSON.stringify(currentTypes) !== JSON.stringify(requestedTypes);
            const totalPages = Math.ceil(total / searchLimit);
            const didLimitReach = totalPages > 0 && page >= totalPages;
            if (!isNewSearch && didLimitReach) {
                return;
            }
            const response = yield this.httpAdapter
                .getOrganizationAdapter()
                .fetchOrganizationMedia({
                limit: searchLimit,
                pageNo: isNewSearch ? 1 : page + 1,
                search: searchTerm || undefined,
                type: searchType,
            }, context);
            if (response.isAborted) {
                return;
            }
            this._organizationMediaData = Object.assign(Object.assign({}, response.data), { data: [
                    ...(isNewSearch ? [] : data),
                    ...(0, mappers_1.organizationMediaMapper)(response.data.data),
                ], searchTerm, type: searchType });
        });
    }
    fetchRecentContacts(bucket, filters, context, paginate) {
        return __awaiter(this, void 0, void 0, function* () {
            const isSearchOn = !!filters.search;
            const currentBucketData = this._recentContactsByBucket[bucket];
            const response = yield this.httpAdapter.getChatAdapter().fetchRecentContacts(Object.assign(Object.assign(Object.assign({ pageNo: paginate ? currentBucketData.pageNo + 1 : 1, limit: utils_1.HHNConstants.PAGINATION_LIMIT }, (filters.search && { search: filters.search })), (filters.groupType && { groupType: filters.groupType })), (filters.userType && {
                userType: JSON.stringify(filters.userType),
            })), context);
            if (!response.isAborted) {
                const recentContactsData = yield (0, mappers_1.recentContactsMapper)(response.data, this.userDirectory, this, this.httpAdapter, this.socketAdapter);
                const updateRecentContacts = (data) => {
                    this._recentContactsByBucket[bucket] = {
                        data,
                        limit: recentContactsData.limit,
                        total: recentContactsData.total,
                        pageNo: recentContactsData.pageNo,
                    };
                };
                if (isSearchOn) {
                    if (paginate) {
                        updateRecentContacts([
                            ...this._recentContactsByBucket[bucket].data,
                            ...recentContactsData.data,
                        ]);
                    }
                    else {
                        this.resetRecentContactsData(bucket);
                        updateRecentContacts(recentContactsData.data);
                    }
                }
                else {
                    if (paginate) {
                        updateRecentContacts([
                            ...this._recentContactsByBucket[bucket].data,
                            ...recentContactsData.data,
                        ]);
                    }
                    else {
                        updateRecentContacts(recentContactsData.data);
                    }
                }
            }
        });
    }
    getTemplateByKey(templateKey, chatInfo, templateFilledBy) {
        const templateResponse = this.templates[templateKey];
        if (templateResponse === null || templateResponse === void 0 ? void 0 : templateResponse.templateMapper) {
            return templateResponse === null || templateResponse === void 0 ? void 0 : templateResponse.templateMapper(templateResponse, {
                fillingBy: templateFilledBy,
                chatInfo,
                organizationUser: this,
            });
        }
        return templateResponse;
    }
    resetRecentContactsData(bucket) {
        if (bucket) {
            this._recentContactsByBucket[bucket] =
                this.createEmptyRecentContactsData();
        }
        else {
            this._recentContactsByBucket = this.createEmptyRecentContactsBuckets();
        }
    }
    createEmptyRecentContactsData() {
        return {
            data: [],
            total: 0,
            pageNo: 0,
            limit: utils_1.HHNConstants.PAGINATION_LIMIT,
        };
    }
    createEmptyRecentContactsBuckets() {
        return {
            [models_1.GROUP_TYPE.PATIENT]: this.createEmptyRecentContactsData(),
            [models_1.GROUP_TYPE.NON_PATIENT]: this.createEmptyRecentContactsData(),
        };
    }
    forwardMessage(selectedContacts, selectedMessages, responseCallback, onTimeout) {
        const forwardTo = selectedContacts.map(contact => {
            return {
                to: contact.id,
                chatType: contact.chatType,
            };
        });
        const messages = selectedMessages.map(message => (0, mappers_1.messageUnmapper)(message));
        const payload = {
            forwardTo,
            messages,
        };
        this.socketAdapter.forwardMessage(payload, responseCallback, onTimeout);
    }
    sendMediaToContacts(selectedContacts, uploadedMediaList, responseCallback, onTimeout) {
        const platformType = this.config.platformType.toLowerCase();
        for (const contact of selectedContacts) {
            for (const uploadedMedia of uploadedMediaList) {
                const timestamp = Date.now();
                const type = (0, MessageUtil_1.fileType)(uploadedMedia.mediaFile);
                const messageBody = (0, MessageUtil_1.mediaMessageBody)(uploadedMedia, type);
                const payload = Object.assign(Object.assign({}, messageBody), { type: models_1.MESSAGE_TYPE.NORMAL, chatType: contact.chatType, messageId: `${platformType}_${(0, MapperUtils_1.randomIdGenerator)(5)}_${timestamp}`, senderTimestamp: `${platformType}_${timestamp}`, to: contact.id });
                this.socketAdapter.sendMessage(payload, responseCallback, onTimeout);
            }
        }
    }
    initOrganization() {
        return __awaiter(this, arguments, void 0, function* (isLoadChatList = true) {
            if (this.removeListeners.length > 0) {
                throw new Error("Panic: Listeners should have been destroyed during unmount!!!");
            }
            this.chatManager.init();
            yield Promise.all([
                this.fetchDepartments(),
                isLoadChatList && this.chatManager.fetchAllChats(),
                isLoadChatList && this.fetchAllTemplates(),
            ]);
            this.removeListeners.push(this.markMessageAsDeliveredListener((message) => {
                if (message.user !== this.organization.userProfile.id) {
                    this.socketAdapter.markMessageAsDelivered(message);
                }
            }));
        });
    }
    removeAllListeners() {
        this.removeListeners.forEach(unsubscribe => unsubscribe());
        this.removeListeners = [];
    }
    switchOrganization() {
        this.destroyOrgExceptBadgeDependencies();
    }
    destroyOrgExceptBadgeDependencies() {
        var _a, _b;
        // Keep alert listeners and hhnStorage active for badge counter updates on non-selected orgs
        // They will be cleaned up on logout via logOut() method
        this.removeAllListeners();
        (_a = this.chatManager) === null || _a === void 0 ? void 0 : _a.destroy();
        (_b = this.labsManager) === null || _b === void 0 ? void 0 : _b.destory();
        this.hhnStorage.resetExceptBadgeDependencies();
    }
    destroy() {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b, _c;
            this.destroyOrgExceptBadgeDependencies();
            (_a = this.alert) === null || _a === void 0 ? void 0 : _a.destroy();
            (_b = this.userDirectory) === null || _b === void 0 ? void 0 : _b.destroy();
            (_c = this.hhnStorage) === null || _c === void 0 ? void 0 : _c.reset();
            yield this.socketAdapter.close();
            this.networkManager.destroy();
            this.socketManager.destroy();
        });
    }
    fetchAllTemplates() {
        return __awaiter(this, void 0, void 0, function* () {
            this._templates = yield this.httpAdapter
                .getOrganizationAdapter()
                .fetchAllTemplates();
        });
    }
    getScheduledVisits(params) {
        return __awaiter(this, void 0, void 0, function* () {
            const key = JSON.stringify(params);
            const existing = this.scheduledVisitsInFlight.get(key);
            if (existing) {
                return existing;
            }
            const request = this.getScheduledVisitsInternal(params);
            this.scheduledVisitsInFlight.set(key, request);
            try {
                return yield request;
            }
            finally {
                this.scheduledVisitsInFlight.delete(key);
            }
        });
    }
    getScheduledVisitsInternal(params) {
        return __awaiter(this, void 0, void 0, function* () {
            return (0, mappers_1.scheduledVisitsMapper)(yield this.httpAdapter.getCommonAdapter().getScheduledVisits(params));
        });
    }
    updateUserProfile(userProfileData) {
        return __awaiter(this, void 0, void 0, function* () {
            const userProfileRequestData = Object.assign(Object.assign({}, userProfileData), { id: this.getOrganization().userProfile.id });
            const userProfileUpdateResponse = yield this.httpAdapter
                .getOrganizationAdapter()
                .updateUserProfile(userProfileRequestData);
            return (0, mappers_1.userInfoMapper)(userProfileUpdateResponse);
        });
    }
    fetchQuickReplies() {
        return __awaiter(this, void 0, void 0, function* () {
            const { quickReplies } = yield this.httpAdapter
                .getOrganizationAdapter()
                .getQuickReplies();
            this._quickReplies = quickReplies;
        });
    }
    addQuickReplies(quickReply) {
        return __awaiter(this, void 0, void 0, function* () {
            const quickReplies = yield this.httpAdapter
                .getOrganizationAdapter()
                .addQuickReplies(quickReply);
            this._quickReplies = quickReplies;
        });
    }
    deleteQuickReplies(index) {
        return __awaiter(this, void 0, void 0, function* () {
            const updatedQuickReplies = yield this.httpAdapter
                .getOrganizationAdapter()
                .deleteQuickReplies(index);
            this._quickReplies = updatedQuickReplies;
        });
    }
    get quickReplies() {
        return this._quickReplies;
    }
    isLabIntegrationEnabled() {
        var _a;
        return ((_a = this.organization.features) === null || _a === void 0 ? void 0 : _a.enableLabIntegration) || false;
    }
    isEmrSyncEnabled() {
        var _a;
        return ((_a = this.organization.features) === null || _a === void 0 ? void 0 : _a.enableEmrSync) || false;
    }
    getEmrSyncMode() {
        var _a, _b;
        return (_b = (_a = this.organization.settings) === null || _a === void 0 ? void 0 : _a.emrSyncMode) !== null && _b !== void 0 ? _b : "manual";
    }
    getEmrSyncInternalGroupIds() {
        var _a, _b;
        return (_b = (_a = this.organization.settings) === null || _a === void 0 ? void 0 : _a.emrSyncInternalGroupIds) !== null && _b !== void 0 ? _b : [];
    }
    updateEmrSettings(request) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            yield this.httpAdapter.getOrganizationAdapter().updateOrgSettings(request);
            const previousSettings = this.organization.settings;
            this.organization = Object.assign(Object.assign({}, this.organization), { settings: Object.assign(Object.assign(Object.assign({ emrId: (_a = previousSettings === null || previousSettings === void 0 ? void 0 : previousSettings.emrId) !== null && _a !== void 0 ? _a : "" }, previousSettings), (request.syncMode !== undefined && {
                    emrSyncMode: request.syncMode,
                })), (request.internalGroupIds !== undefined && {
                    emrSyncInternalGroupIds: request.internalGroupIds,
                })) });
            this.changeListeners.forEach(l => l.cb(this));
        });
    }
    isAutoAckEnabled() {
        var _a;
        return ((_a = this.organization.features) === null || _a === void 0 ? void 0 : _a.autoAskAcknowledgement) || false;
    }
    fetchAutoAckEvents() {
        return __awaiter(this, void 0, void 0, function* () {
            const events = yield this.httpAdapter
                .getOrganizationAdapter()
                .getAutoAckEvents();
            return (0, AutoAckMapper_1.autoAckEventMapper)(events);
        });
    }
    fetchAutoAckRules() {
        return __awaiter(this, void 0, void 0, function* () {
            const rules = yield this.httpAdapter
                .getOrganizationAdapter()
                .getAutoAckRules();
            return (0, AutoAckMapper_1.autoAckRuleMapper)(rules);
        });
    }
    saveAutoAckRules(rules) {
        return __awaiter(this, void 0, void 0, function* () {
            const saved = yield this.httpAdapter
                .getOrganizationAdapter()
                .replaceAutoAckRules(rules.map(AutoAckMapper_1.autoAckRuleToRequest));
            return (0, AutoAckMapper_1.autoAckRuleMapper)(saved);
        });
    }
    fetchGeneralSettings() {
        return this.httpAdapter.getOrganizationAdapter().getOrgGeneralSettings();
    }
    saveGeneralSettings(request) {
        return this.httpAdapter
            .getOrganizationAdapter()
            .updateOrgGeneralSettings(request);
    }
}
exports.default = OrganizationUser;
